import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Comment,
  CommentCreate,
  ReactionKind,
  ReactionState,
  SubscriptionState,
  VideoDetail,
} from '@streamr/shared';
import { api } from '../client';
import { routes } from '../routes';
import { usePagedQuery } from './useDiscovery';

/**
 * Like/dislike. The API keys one row on (user, video) with a kind, so "liked
 * and disliked at once" is unrepresentable; sending the kind already held
 * clears it, which is what the second click on an active button does here.
 */
export const useReaction = (videoId: string) => {
  const queryClient = useQueryClient();

  const applyToVideo = (state: ReactionState): void => {
    queryClient.setQueryData<VideoDetail>(['video', videoId], (current) =>
      current === undefined
        ? current
        : {
            ...current,
            likeCount: state.likeCount,
            dislikeCount: state.dislikeCount,
            viewerReaction: state.kind,
          },
    );
  };

  const react = useMutation({
    mutationFn: (kind: ReactionKind) =>
      api.put<ReactionState>(routes.reactions(videoId), { kind }),
    onSuccess: applyToVideo,
  });

  const clear = useMutation({
    mutationFn: () => api.delete<ReactionState>(routes.reactions(videoId)),
    onSuccess: applyToVideo,
  });

  return {
    react,
    clear,
    toggle: (kind: ReactionKind, current: ReactionKind | null | undefined) =>
      current === kind ? clear.mutate() : react.mutate(kind),
    pending: react.isPending || clear.isPending,
  };
};

export const useComments = (videoId: string) =>
  usePagedQuery<Comment>(['comments', videoId], routes.comments.list(), { videoId }, { limit: 20 });

/**
 * Replies are fetched per thread, one level deep, and only when expanded. The
 * flat /comments collection is keyed by videoId, so a reply query carries both
 * the videoId and the parent comment id.
 */
export const useReplies = (videoId: string, commentId: string, enabled: boolean) =>
  usePagedQuery<Comment>(
    ['comment-replies', commentId],
    routes.comments.list(),
    { videoId, parentId: commentId },
    { enabled, limit: 20 },
  );

export const usePostComment = (videoId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    // videoId rides in the body for the flat /comments collection, not the path.
    mutationFn: (input: CommentCreate) =>
      api.post<Comment>(routes.comments.list(), { ...input, videoId }),
    onSuccess: (created) => {
      if (created.parentId === null) {
        void queryClient.invalidateQueries({ queryKey: ['comments', videoId] });
      } else {
        void queryClient.invalidateQueries({ queryKey: ['comment-replies', created.parentId] });
        void queryClient.invalidateQueries({ queryKey: ['comments', videoId] });
      }
    },
  });
};

export const useDeleteComment = (videoId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => api.delete<void>(routes.comments.detail(commentId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments', videoId] });
    },
  });
};

/**
 * Subscribe/unsubscribe. Keyed by channel id rather than handle: a handle can
 * be changed by its owner (channelUpdateSchema allows it) and a subscription
 * pointing at a stale handle would silently target nothing.
 */
export const useSubscription = (channelId: string, handle?: string) => {
  const queryClient = useQueryClient();

  const settle = (state: SubscriptionState): void => {
    if (handle !== undefined) {
      queryClient.setQueryData(['channel', handle], (current: unknown) =>
        current === undefined
          ? current
          : {
              ...(current as object),
              subscriberCount: state.subscriberCount,
              viewerIsSubscribed: state.subscribed,
            },
      );
    }
    void queryClient.invalidateQueries({ queryKey: ['feed'] });
  };

  const subscribe = useMutation({
    mutationFn: () => api.put<SubscriptionState>(routes.subscriptions(channelId)),
    onSuccess: settle,
  });

  const unsubscribe = useMutation({
    mutationFn: () => api.delete<SubscriptionState>(routes.subscriptions(channelId)),
    onSuccess: settle,
  });

  return {
    subscribe,
    unsubscribe,
    pending: subscribe.isPending || unsubscribe.isPending,
    toggle: (isSubscribed: boolean) => (isSubscribed ? unsubscribe.mutate() : subscribe.mutate()),
  };
};
